import { DataNode } from 'antd/lib/tree';
import { FailureSummary } from './enforcement-flow/failureSummary.type';

export enum ActionMethod {
  CREATE = 'CREATE',
  READ = 'READ',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE'
}

export type FieldValue =
  | string
  | number
  | boolean
  | undefined
  | null
  | Record<string, unknown>
  | DataNode[];

export interface KeyValEnum {
  key: string;
  value: string;
  disabled?: boolean;
  defaultChecked?: boolean;
  picture?: string;
  email?: string;
  text?: string;
  hidePicture?: boolean;
}

export enum InputFieldType {
  Select = 'Select',
  String = 'String',
  Number = 'Number',
  Date = 'Date',
  DatePicker = 'DatePicker',
  Boolean = 'Boolean',
  Enum = 'Enum',
  Dictionary = 'Dictionary',
  ManagedDictionary = 'ManagedDictionary',
  File = 'File',
  UploadFile = 'UploadFile',
  FileInformation = 'FileInformation',
  Url = 'Url',
  MultiSelect = 'MultiSelect',
  TextArea = 'TextArea',
  RichTextArea = 'RichTextArea',
  Mentions = 'Mentions',
  Info = 'Info',
  TreeView = 'TreeView',
  FileDecisionTagging = 'FileDecisionTagging',
  Nested = 'Nested',
  Array = 'Array',
  JsonEditor = 'JsonEditor',
  RecoBatchTransferIds = 'Reco:BatchTransferIds',
  AlertMessage = 'AlertMessage',
  Button = 'Button',
  PreviewHtmlModal = 'PreviewHtmlModal'
}

export enum EnumDisplayType {
  List = 'List',
  Radio = 'Radio'
}

export enum DictionaryDisplayType {
  List = 'List',
  Radio = 'Radio',
  Checkbox = 'Checkbox'
}

export enum BooleanDisplayType {
  Checkbox = 'Checkbox',
  Toggle = 'Toggle'
}

export interface ControlActionField {
  id?: number;
  name: string;
  displayName?: string;
  description?: string;
  type: InputFieldType;
  isRequired?: boolean;
  isUnique?: boolean;
  enumsDisplayType?: EnumDisplayType;
  booleanDisplayType?: BooleanDisplayType;
  enums?: string[] | { label: string; value: string; disabled?: boolean }[];
  useEnums?: (initialValue?: { value: string; label: string }[]) => {
    enums: { value: string; label: string }[];
    loading: boolean;
    disabled?: boolean;
  };
  tree?: DataNode[];
  dictionaryDisplayType?: DictionaryDisplayType;
  dictionary?: KeyValEnum[];
  useKeyAsLabel?: boolean;
  multiSelect?: boolean;
  multiSelectMode?: 'tags' | 'multiple';
  value?: FieldValue | FieldValue[];
  defaultValue?: FieldValue | FieldValue[];
  accept?: string[];
  bold?: boolean;
  boldValue?: boolean;
  delete?: boolean;
  placeholder?: string;
  disabled?: boolean;
  disabledTooltip?: string;
  nestedFields?: ControlActionField[];
  innerField?: ControlActionField;
  isHidden?: boolean;
  optionDisabled?: boolean;
  mode?: string;
  dictionarySelectedOptionsRequiredLength?: number;
  dictionaryAllCheckboxesRequired?: boolean;
  lowerText?: string;
  label?: string;
  shouldFormatDisplayValues?: boolean;
  shouldCapitalizeDisplayValues?: boolean;
  metadata?: Record<string, unknown>;
  helperText?: string;
  warningText?: string;
  informationText?: string;
  informationTextWrapsChildren?: boolean;
  header?: string;
  searchByKeyAndValue?: boolean;
  allowClear?: boolean;
  keepEnabledWhenEmpty?: boolean;
  disableFutureDates?: boolean;
  spiiData?: {
    isSpii: boolean;
    partnerName?: string;
    useCryptoService?: boolean;
  };
  uiOptions?: {
    showFieldAndWidgetInline?: boolean;
    marginTop?: number;
    borderedWhenDisabled?: boolean;
    color?: 'secondary' | 'success' | 'warning' | 'danger';
  };
  collapseGroup?: CollapseTypes;
  collapseGroupDisplayName?: string;
  nestedCollapseGroup?: CollapseTypes;
  nestedCollapseGroupDisplayName?: string;
  isExpendByDefault?: boolean;
  fieldWidth?: string;
  isLastInRow?: boolean;
  collapseWidth?: string;
  direction?: DirectionTypes;
  showOnRemove?: boolean;
}

export type ControlActionInfoRowValue = {
  text: string;
  href?: string;
};

export type ControlActionInfoRow = {
  label: string;
  value: string | ControlActionInfoRowValue[];
};

export type ControlActionInfo = {
  name: string;
  displayName?: string;
  value: string;
  type: InputFieldType.Info;
  bold?: boolean;
  boldValue?: boolean;
  isHidden?: boolean;
  metadata?: Record<string, unknown>;
  helperText?: string;
  warningText?: string;
  informationText?: string;
  header?: string;
  collapseGroup?: CollapseTypes;
  collapseGroupDisplayName?: string;
  nestedCollapseGroup?: CollapseTypes;
  nestedCollapseGroupDisplayName?: string;
  isExpendByDefault?: boolean;
  collapseWidth?: string;
  dataRows?: ControlActionInfoRow[];
  dataRowsGap?: 's' | 'm' | 'l' | 'xl';
  showDivider?: boolean;
};

export interface ActionFields {
  fields: ControlActionField[];
  fieldsGroup?: ControlActionGroup;
  refetch?: () => void;
  failureSummary?: FailureSummary[];
}
export interface ControlActionGroup {
  fieldsSchema: Record<string, ControlActionField>;
  fieldsValues: Group[];
  fieldsSettings?: {
    shouldHideRemove?: boolean;
    maxItems?: number;
    buttonText?: string;
    disableAutoInit?: boolean;
    enableClear?: boolean;
    showFieldsOnRemove?: boolean;
  };
}

export interface Group {
  delete?: boolean;
  cleared?: boolean;
  markedForRemoval?: boolean;
  index: number;
  values: Record<string, ControlActionField>;
}

export type ActionType = 'single' | 'bulk' | 'global';

export enum ActionableEntities {
  Task = 'task',
  BusinessVerificationTask = 'businessVerificationTask',
  Escalation = 'escalation',
  Organization = 'organization',
  OrganizationEscalation = 'organizationEscalation',
  Vendor = 'vendor',
  Payment = 'payment',
  FundingSource = 'fundingSource',
  DeliveryMethod = 'deliveryMethod',
  User = 'user',
  Project = 'project',
  Partner = 'partner',
  Adjustment = 'adjustment',
  Check = 'check',
  Card = 'card',
  Ach = 'ach',
  RejectCheck = 'rejectCheck',
  Unclassified = 'unclassified',
  MelioDbCase = 'melioDbCase',
  Chargeback = 'chargeback',
  UBO = 'ubo',
  Oki = 'oki',
  Ip = 'ip',
  PaymentIp = 'paymentIp',
  PayorIp = 'payorIp',
  PayeeIp = 'payeeIp',
  UpdateIp = 'updateIp',
  VirtualCardAccount = 'virtualCardAccount',
  FraudReferralCase = 'fraudReferralCase',
  AtoAutomationCase = 'atoAutomationCase',
  ArMonitoringRun = 'arMonitoringRun',
  PromoCode = 'promoCode',
  FeatureStrategy = 'featureStrategy',
  StrategyRule = 'strategyRule',
  TagDefinition = 'tagDefinition'
}

type ActionButtonType =
  | 'dashed'
  | 'default'
  | 'ghost'
  | 'link'
  | 'primary'
  | 'text';

type ButtonDisplayType = 'icon-only';

type Hook<T = { fields: ControlActionField[] }> = ({
  fields,
  instance
}: {
  fields: ControlActionField[];
  instance: unknown;
}) => T;

export interface ControlAction<
  HookType = Hook,
  BooleanHookType = Hook<boolean>,
  DisableHookType = never
> {
  groupKey?: string;
  name: string;
  entity: ActionableEntities;
  displayName?: string;
  type: ActionType;
  method?: ActionMethod;
  endpoint: string;
  fields?: (ControlActionField | ControlActionInfo)[];
  fieldsGroup?: ControlActionGroup;
  isSeparate?: boolean;
  parentDropDownName?: string;
  description?: string;
  buttonType?: ActionButtonType;
  icon?: string;
  color?: string;
  download?: boolean;
  hideFooter?: boolean;
  backgroundColor?: string;
  callbacks?: {
    onFinishAndClose?: boolean;
    onFinishAndOpen?: boolean;
    onFinishAndRefresh?: boolean;
    closeOnFinish?: boolean;
    refreshOnFinish?: boolean;
  };
  buttonDisplayType?: ButtonDisplayType;
  skipEntity?: boolean;
  skipShouldDisplayActionCheck?: boolean;
  hooks?: {
    load?: HookType;
    change?: Record<string, HookType>;
    shouldDisplayAction?: BooleanHookType;
    shouldDisableAction?: DisableHookType | DisableHookType[];
  };
  danger?: boolean;
  actionDrawerTitle?: string;
  showSuccessMessage?: boolean;
  customSuccessMessage?: string;
  customErrorMessage?: string;
  keepOpenOnError?: boolean;
  showLoadingUntilCompleted?: boolean;
  submitLoader?: {
    messageKey: string;
    whenFieldEquals?: {
      name: string;
      value: unknown;
    };
  };
  drawerWidth?: string;
  showInvalidError?: boolean;
  disableSubmitOnPristine?: boolean;
  translationKeys?: {
    submitButton?: string;
  };
  header?: {
    alert?: {
      type: 'info' | 'warning' | 'error' | 'success';
      message: string;
      dismissible?: boolean;
    };
  };
}

export interface Control {
  viewName: string;
  actions: ControlAction[];
}

export enum ConfirmationPopupType {
  Warning = 'warning',
  Information = 'information',
  Share = 'share',
  Money = 'money',
  Success = 'success',
  Critical = 'critical'
}

export type ConfirmationPopup = {
  title: string;
  type?: ConfirmationPopupType;
  message?: string;
  fields?: ControlActionField[];
  submitText?: string;
  cancelText?: string;
  hideCancelButton?: boolean;
  navigateUrl?: string;
};

export type ActionResponse = {
  message?: string;
  confirmationPopup?: ConfirmationPopup;
};

export type WidgetChange = (
  fieldName: string,
  newValue:
    | FieldValue
    | FieldValue[]
    | Record<string, FieldValue | FieldValue[]>,
  index?: number
) => void;

export interface WidgetProps {
  field: ControlActionField;
  groupIndex?: number;
  onChange: WidgetChange;
  isFormLoading?: boolean;
  partnerName?: string;
}

export type FieldWidgetContainerProps = {
  field: ControlActionField;
  onChange: WidgetChange;
  isFormLoading?: boolean;
  partnerName?: string;
};

export enum CollapseTypes {
  KycLegalInformation = 'Legal Information',
  KycAddresses = 'Addresses',
  kycIndustryInformation = 'Industry Information',
  KycReferenceDBA = 'ReferenceDBA',
  KycReferenceLegalName = 'ReferenceLegalName',
  KycReferenceTaxId = 'ReferenceTaxId',
  KycReferenceTaxIdType = 'ReferenceTaxIdType',
  KycReferenceDOB = 'ReferenceDOB',
  KycReferenceBusinessType = 'ReferenceBusinessType',
  KycReferencePhone = 'ReferencePhone',
  KycReferenceCompanyWebsite = 'ReferenceCompanyWebsite',
  KycReferenceCompanyDescription = 'ReferenceCompanyDescription',
  KycReferenceBusinessZipCode = 'ReferenceBusinessZipCode',
  KycReferenceZipCode = 'ReferenceZipCode',
  KycReferenceSubIndustry = 'ReferenceSubIndustry'
}

export enum DirectionTypes {
  Horizontal = 'horizontal',
  Vertical = 'vertical'
}
